import { useEffect, useMemo, useRef, useState } from 'react'
import type { Lesson, LessonNoteEditingPayload } from '@/types'
import { getEchoClient } from './echoClient'

export type LessonRealtimeUpdateType = 'status' | 'note' | 'homework' | 'word'

export type { LessonNoteEditingPayload, LessonNoteEditingState } from '@/types'

export type LessonPresenceUser = { id: number; name: string }

const ECHO_DEBUG =
  import.meta.env.DEV || String(import.meta.env.VITE_ECHO_DEBUG || '') === 'true'

function echoDevLog(...args: unknown[]) {
  if (ECHO_DEBUG) {
    console.info('[Echo]', ...args)
  }
}

/** Короткий сигнал при входе другого пользователя в presence-канал урока (Web Audio API). */
function playJoinSound() {
  const AC = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
  if (!AC) return
  const ctx = new AC()
  const osc = ctx.createOscillator()
  const gain = ctx.createGain()
  osc.connect(gain)
  gain.connect(ctx.destination)
  osc.type = 'sine'
  osc.frequency.setValueAtTime(880, ctx.currentTime)
  gain.gain.setValueAtTime(0.1, ctx.currentTime)
  gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.4)
  osc.start(ctx.currentTime)
  osc.stop(ctx.currentTime + 0.4)
}

function normalizePresenceUsers(raw: unknown[]): LessonPresenceUser[] {
  return raw.map((u) => {
    const o = u as { id?: unknown; name?: unknown }
    return {
      id: Number(o.id),
      name: String(o.name ?? ''),
    }
  }).filter((u) => Number.isFinite(u.id))
}

function leaveLessonPresenceChannel(echo: ReturnType<typeof getEchoClient>, channelName: string) {
  try {
    /** socket.io connector: уходит и presence-, и private-вариант по базовому имени */
    echo.leave(channelName)
  } catch {
    // ignore
  }
}

export function useLessonRealtime(
  lessonId: number | null,
  onUpdate: (type: LessonRealtimeUpdateType) => void,
  options?: {
    onNoteEditing?: (payload: LessonNoteEditingPayload) => void
    authUserId?: number | null
  },
) {
  const [isConnected, setIsConnected] = useState(false)
  const [presentUsers, setPresentUsers] = useState<LessonPresenceUser[]>([])
  const onUpdateRef = useRef(onUpdate)
  const onNoteEditingRef = useRef(options?.onNoteEditing)
  const authUserIdRef = useRef(options?.authUserId)

  onUpdateRef.current = onUpdate
  onNoteEditingRef.current = options?.onNoteEditing
  authUserIdRef.current = options?.authUserId

  useEffect(() => {
    if (!Number.isFinite(lessonId)) {
      setIsConnected(false)
      setPresentUsers([])
      return
    }

    let didJoin = false
    const echo = getEchoClient()
    const channelName = `lesson.${lessonId}`
    const presenceKey = `presence-${channelName}`

    setIsConnected(false)
    setPresentUsers([])

    const connector = echo.connector as { channels?: Record<string, unknown> }
    if (connector.channels?.[presenceKey]) {
      leaveLessonPresenceChannel(echo, channelName)
    }

    const channel = echo.join(channelName)

    type ChannelWithLifecycle = typeof channel & {
      subscribed?: (cb: () => void) => typeof channel
      error?: (cb: (err: unknown) => void) => typeof channel
    }
    const ch = channel as ChannelWithLifecycle

    if (typeof ch.subscribed === 'function') {
      ch.subscribed(() => {
        setIsConnected(true)
        echoDevLog(
          `Подписка на presence-${channelName} подтверждена (см. также POST /api/broadcasting/auth в Network)`,
        )
      })
    } else {
      setIsConnected(true)
      echoDevLog(`presence-${channelName}: колбэка subscribed() нет — подписка считается активной`)
    }

    if (typeof ch.error === 'function') {
      ch.error((err) => {
        setIsConnected(false)
        console.error('[Echo] Ошибка подписки на presence-канал', channelName, err)
      })
    }

    channel
      .here((users: unknown[]) => {
        setPresentUsers(normalizePresenceUsers(users))
      })
      .joining((user: unknown) => {
        const u = normalizePresenceUsers([user])[0]
        if (!u) return
        const selfId = authUserIdRef.current
        if (selfId != null && u.id === selfId) return
        playJoinSound()
        setPresentUsers((prev) => (prev.some((x) => x.id === u.id) ? prev : [...prev, u]))
      })
      .leaving((user: unknown) => {
        const u = normalizePresenceUsers([user])[0]
        if (!u) return
        setPresentUsers((prev) => prev.filter((x) => x.id !== u.id))
      })

    channel.listen('.lesson.status.changed', () => onUpdateRef.current('status'))
    channel.listen('.lesson.note.changed', () => onUpdateRef.current('note'))
    channel.listen('.lesson.homework.changed', () => onUpdateRef.current('homework'))
    channel.listen('.lesson.word.added', () => onUpdateRef.current('word'))
    channel.listen('.lesson.word.removed', () => onUpdateRef.current('word'))
    channel.listen('.lesson.note.editing', (payload: LessonNoteEditingPayload) => {
      onNoteEditingRef.current?.(payload)
    })

    didJoin = true

    return () => {
      if (didJoin) {
        leaveLessonPresenceChannel(echo, channelName)
        didJoin = false
      }
      setIsConnected(false)
      setPresentUsers([])
    }
  }, [lessonId])

  return { isConnected, presentUsers }
}

/**
 * Подписка на presence-каналы уроков **со списка** (интерфейс преподавателя на `/lessons`).
 * Страница деталей урока не монтируется — без этого хука WebSocket `subscribe` не уходит.
 * Подписываемся на уроки в статусе `in_progress`: свои (teacher_id) или все в списке для админа.
 */
export function useTeachingLessonsListRealtime(
  lessons: Lesson[],
  authUserId: number | null,
  isAdmin: boolean,
  onRefresh: () => void,
) {
  const onRefreshRef = useRef(onRefresh)
  onRefreshRef.current = onRefresh

  const lessonIdsKey = useMemo(() => {
    const ids = lessons
      .filter((l) => {
        if (l.status !== 'in_progress') return false
        if (isAdmin) return true
        return authUserId != null && l.teacher_id === authUserId
      })
      .map((l) => l.id)
      .sort((a, b) => a - b)
    return ids.join(',')
  }, [lessons, authUserId, isAdmin])

  useEffect(() => {
    if (!lessonIdsKey) return

    const lessonIds = lessonIdsKey
      .split(',')
      .map((s) => parseInt(s, 10))
      .filter((n) => Number.isFinite(n))

    const echo = getEchoClient()
    const channelNames: string[] = []

    for (const id of lessonIds) {
      const channelName = `lesson.${id}`
      channelNames.push(channelName)

      const refresh = () => onRefreshRef.current()
      echo
        .join(channelName)
        .here(() => {})
        .joining(() => {})
        .leaving(() => {})
        .listen('.lesson.status.changed', refresh)
        .listen('.lesson.note.changed', refresh)
        .listen('.lesson.homework.changed', refresh)
        .listen('.lesson.note.editing', refresh)

      echoDevLog(`Список уроков: подписка на presence-${channelName}`)
    }

    return () => {
      for (const channelName of channelNames) {
        leaveLessonPresenceChannel(echo, channelName)
      }
    }
  }, [lessonIdsKey])
}
